import React, { useState, useEffect } from 'react';
import { accountingApi } from '../../../services/accountingApi';

const DEFAULT_ROWS = 10;
const emptyLine = () => ({ account_id: '', account_name: '', debit: '', credit: '' });
const makeLines = () => Array.from({ length: DEFAULT_ROWS }, emptyLine);

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmt = (n) => '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const JournalEntry = () => {
  const [entries, setEntries]   = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [showModal, setShowModal] = useState(false);
  const [lines, setLines]       = useState(makeLines());
  const [header, setHeader]     = useState({ ref: '', date: todayStr(), notes: '' });

  const loadEntries = () => {
    setLoading(true);
    Promise.all([accountingApi.getJournalEntries(), accountingApi.getAccounts()])
      .then(([jeRes, acRes]) => {
        const toArr = r => Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setEntries(toArr(jeRes));
        setAccounts(toArr(acRes));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEntries(); }, []);

  const totalDebit  = lines.reduce((s, l) => s + (parseFloat(l.debit)  || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced    = Math.abs(totalDebit - totalCredit) < 0.001 && totalDebit > 0;

  const updateLine = (i, field, val) =>
    setLines(prev => prev.map((l, idx) => {
      if (idx !== i) return l;
      if (field === 'account_id') {
        const acc = accounts.find(a => String(a.id) === String(val));
        return { ...l, account_id: val, account_name: acc?.name || '' };
      }
      return { ...l, [field]: val };
    }));

  const openModal = () => {
    setLines(makeLines());
    setHeader({ ref: '', date: todayStr(), notes: '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    const usedLines = lines.filter(l => l.account_id && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0));
    if (!usedLines.length || !balanced) return;
    setSaving(true);
    try {
      await accountingApi.createJournalEntry({
        ref_no: header.ref,
        transaction_date: header.date,
        additional_notes: header.notes,
        entries: usedLines.map(l => ({
          account_id: l.account_id,
          debit:  parseFloat(l.debit)  || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      });
      setShowModal(false);
      loadEntries();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this journal entry?')) return;
    try {
      await accountingApi.deleteJournalEntry(id);
      loadEntries();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  const filtered = entries.filter(e => {
    const desc = e.description || e.narration || '';
    const ref  = e.ref_no || e.id || '';
    return desc.toLowerCase().includes(search.toLowerCase()) || String(ref).includes(search);
  });

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading journal entries...</div>;
  if (error)   return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div className="acc-stats-row">
        {[
          { label: 'Total Entries', value: entries.length, color: 'blue' },
          { label: 'Posted', value: entries.filter(e => (e.status || '').toLowerCase() === 'approved').length, color: 'green' },
          { label: 'Pending', value: entries.filter(e => (e.status || '').toLowerCase() !== 'approved').length, color: 'orange' },
          { label: 'Total Debits', value: `$${entries.reduce((s, e) => s + (parseFloat(e.debit) || 0), 0).toLocaleString()}`, color: 'purple' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`}>
            <div className="acc-stat-label">{s.label}</div>
            <div className="acc-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="table-content-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 className="acc-card-title" style={{ margin: 0 }}>Journal Entries</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--acc-surface)', padding: '0.2rem 0.5rem', borderRadius: 'var(--acc-radius)', border: '1px solid var(--acc-border)', boxShadow: 'var(--acc-shadow-sm)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--acc-text-muted)', marginLeft: '0.5rem' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="tabs-search-input"
                placeholder="Search entries..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ border: 'none', outline: 'none', padding: '0.4rem', fontSize: '0.9rem', background: 'transparent', minWidth: '180px', color: 'var(--acc-text-main)' }}
              />
            </div>
            <button className="acc-btn-primary" onClick={openModal}>
              <span className="plus-icon">+</span> New Entry
            </button>
          </div>
        </div>

        <div className="acc-table-wrapper products-table-container">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Entry #</th>
                <th>Date</th>
                <th>Reference</th>
                <th>Description</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id}>
                  <td><span style={{ fontWeight: 600, color: 'var(--etpos-primary,#6366f1)' }}>{e.id}</span></td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{e.transaction_date || e.date || '—'}</td>
                  <td>{e.ref_no || e.ref || '—'}</td>
                  <td>{e.additional_notes || e.description || '—'}</td>
                  <td className="text-right">{fmt(e.debit)}</td>
                  <td className="text-right">{fmt(e.credit)}</td>
                  <td className="text-center">
                    <span className={`acc-badge ${(e.status || '').toLowerCase() === 'approved' ? 'acc-badge-green' : 'acc-badge-yellow'}`}>{e.status || 'Pending'}</span>
                  </td>
                  <td className="text-center">
                    <button onClick={() => handleDelete(e.id)}
                      style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem', color: '#ef4444' }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8}><div className="acc-empty"><div className="acc-empty-icon">📒</div><h4>No entries found</h4></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add Journal Entry Modal ── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '8px', width: '95%', maxWidth: '720px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.2)' }}
          >
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>Add Journal Entry</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {/* Header fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Reference No</label>
                  <input
                    value={header.ref}
                    onChange={e => setHeader(p => ({ ...p, ref: e.target.value }))}
                    placeholder="e.g. INV-001"
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', fontSize: '0.875rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Journal Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    value={header.date}
                    onChange={e => setHeader(p => ({ ...p, date: e.target.value }))}
                    placeholder="MM/DD/YYYY HH:MM AM"
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', fontSize: '0.875rem' }}
                  />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Additional Notes</label>
                  <textarea
                    value={header.notes}
                    onChange={e => setHeader(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={3}
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.875rem' }}
                  />
                </div>
              </div>

              {/* Lines table */}
              <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', background: '#f8fafc' }}>
                  <colgroup>
                    <col style={{ width: 44 }} />
                    <col />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>#</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Account</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Debit</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Credit</th>
                    </tr>
                  </thead>
                  <tbody style={{ background: '#fff' }}>
                    {lines.map((l, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: 'center', padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 500 }}>{i + 1}</td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                          <select
                            value={l.account_id}
                            onChange={e => updateLine(i, 'account_id', e.target.value)}
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', background: '#fff', fontSize: '0.85rem' }}
                          >
                            <option value="">Please select</option>
                            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                          <input
                            type="number"
                            min="0"
                            value={l.debit}
                            onChange={e => updateLine(i, 'debit', e.target.value)}
                            placeholder="0.00"
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                          <input
                            type="number"
                            min="0"
                            value={l.credit}
                            onChange={e => updateLine(i, 'credit', e.target.value)}
                            placeholder="0.00"
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', fontSize: '0.85rem' }}
                          />
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr>
                      <td colSpan={2} style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: '#1e293b' }}>Total</td>
                      <td style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: balanced ? '#10b981' : totalDebit > 0 ? '#ef4444' : '#1e293b' }}>
                        {totalDebit > 0 ? `$${totalDebit.toFixed(2)}` : ''}
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: balanced ? '#10b981' : totalCredit > 0 ? '#ef4444' : '#1e293b' }}>
                        {totalCredit > 0 ? `$${totalCredit.toFixed(2)}` : ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!balanced && totalDebit > 0 && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#ef4444', fontSize: '0.85rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚠ Not balanced — Difference: ${Math.abs(totalDebit - totalCredit).toFixed(2)}
                </div>
              )}

              <button
                onClick={() => setLines(prev => [...prev, emptyLine()])}
                style={{ marginTop: '1rem', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '6px', padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.875rem', color: '#64748b', width: '100%', fontWeight: 600, transition: 'all 0.15s ease' }}
                onMouseEnter={e => { e.target.style.borderColor = '#94a3b8'; e.target.style.color = '#475569'; e.target.style.background = '#f1f5f9'; }}
                onMouseLeave={e => { e.target.style.borderColor = '#cbd5e1'; e.target.style.color = '#64748b'; e.target.style.background = '#f8fafc'; }}
              >
                + Add Row
              </button>
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: '#f8fafc' }}>
              <button
                onClick={handleSave}
                disabled={saving || !balanced}
                style={{ background: saving || !balanced ? '#a5b4fc' : '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem 2.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: saving || !balanced ? 'not-allowed' : 'pointer', transition: 'background 0.15s ease' }}
                onMouseEnter={e => { if (!saving && balanced) e.target.style.background = '#4c1d95'; }}
                onMouseLeave={e => { if (!saving && balanced) e.target.style.background = '#5b21b6'; }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem 2.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease' }}
                onMouseEnter={e => e.target.style.background = '#1e293b'}
                onMouseLeave={e => e.target.style.background = '#334155'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntry;
